import pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { createArchitectureReportTemplate } from './pdfTemplates';
import {
  getMetarouterLogoDataUrl,
  getMetarouterMarkDataUrl,
  getArchitectLogoDataUrl,
  fontData,
  preloadFonts
} from './pdfAssets';

// Preload assets for use in PDFs
let metarouterLogoDataUrl: string = '';
let metarouterMarkDataUrl: string = '';
let architectLogoDataUrl: string = '';

// Function to ensure assets are loaded before generating PDFs
const ensureAssetsLoaded = async (): Promise<void> => {
  if (!metarouterLogoDataUrl || !metarouterMarkDataUrl) {
    metarouterLogoDataUrl = await getMetarouterLogoDataUrl();
    metarouterMarkDataUrl = await getMetarouterMarkDataUrl();
    architectLogoDataUrl = await getArchitectLogoDataUrl();
  }
  
  if (!fontData.regular) {
    const fontsLoaded = await preloadFonts();
    if (!fontsLoaded) {
      throw new Error('Failed to load fonts for PDF generation');
    }
  }
};

// Interface for PDF data
export interface PdfData {
  title: string;
  description: string;
  author: string;
  createdAt: string;
  projectId: string;
  nodeCount: number;
  edgeCount: number;
  sources: string[];
  destinations: string[];
  [key: string]: any; // Allow for additional metadata
}

// Initialize pdfMake with custom fonts
const initPdfMake = async () => {
  try {
    // Ensure fonts are loaded
    await ensureAssetsLoaded();
    
    // Verify font data is loaded
    if (!fontData.regular || !fontData.bold) {
      console.error('Font data not loaded properly:', {
        regularLoaded: !!fontData.regular,
        boldLoaded: !!fontData.bold,
        mediumLoaded: !!fontData.medium,
        semiBoldLoaded: !!fontData.semiBold
      });
      throw new Error('Font data not loaded properly');
    }
    
    // Create the virtual file system with font data
    const vfs = {
      'IBMPlexSans-Regular.ttf': fontData.regular,
      'IBMPlexSans-Bold.ttf': fontData.bold,
      'IBMPlexSans-Medium.ttf': fontData.medium,
      'IBMPlexSans-SemiBold.ttf': fontData.semiBold,
    };

    
    // Set up pdfMake with our fonts
    pdfMake.vfs = vfs;
    
    // Define the fonts
    pdfMake.fonts = {
      IBMPlexSans: {
        normal: 'IBMPlexSans-Regular.ttf',
        bold: 'IBMPlexSans-Bold.ttf',
        italics: 'IBMPlexSans-Regular.ttf',
        bolditalics: 'IBMPlexSans-Bold.ttf'
      }
    };
    
    // Verify the default style is set correctly in document definition
    const defaultStyle = {
      font: 'IBMPlexSans'
    };
    
    console.log('pdfMake initialized with custom fonts and default style:', defaultStyle);
    return true;
  } catch (error) {
    console.error('Error initializing pdfMake:', error);
    return false;
  }
};

/**
 * Generates a PDF document with MetaRouter branding and project data
 * @param data Project metadata for the PDF
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @returns PDF document definition
 */
export const generatePDF = async (data: PdfData, diagramImageBase64?: string): Promise<TDocumentDefinitions> => {
  // Initialize pdfMake with our custom fonts
  const initialized = await initPdfMake();
  if (!initialized) {
    throw new Error('Failed to initialize pdfMake with custom fonts');
  }
  
  // Create document definition with explicit defaultStyle
  const docDefinition = createArchitectureReportTemplate(
    data,
    diagramImageBase64,
    {
      logoUrl: metarouterLogoDataUrl,
      markUrl: metarouterMarkDataUrl,
      architectUrl: architectLogoDataUrl,
    }
  );
  
  // Ensure defaultStyle is set
  if (!docDefinition.defaultStyle) {
    docDefinition.defaultStyle = { font: 'IBMPlexSans' };
  } else if (!docDefinition.defaultStyle.font) {
    docDefinition.defaultStyle.font = 'IBMPlexSans';
  }
  
  return docDefinition;
};

/**
 * Downloads a PDF with the given data and diagram image
 * @param data Project metadata for the PDF
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @param filename Optional filename for the downloaded PDF (defaults to project title)
 */
export const downloadPDF = async (data: PdfData, diagramImageBase64?: string, filename?: string): Promise<void> => {
  try {
    const docDefinition = await generatePDF(data, diagramImageBase64);
    
    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    
    // Download the PDF
    pdfDocGenerator.download(filename || `${data.title.replace(/\s+/g, '_')}_MetaRouter_io.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Example usage of the PDF generator
 */
export const generateExamplePDF = async (): Promise<void> => {
  try {
    
    const sampleData: PdfData = {
      title: 'Sample Data Architecture',
      description: 'This is a sample data architecture design created with MetaRouter Architect.',
      author: 'John Doe',
      createdAt: new Date().toISOString(),
      projectId: 'proj-123456',
      nodeCount: 12,
      edgeCount: 15,
      sources: ['Google Analytics', 'Segment', 'Customer Database'],
      destinations: ['Snowflake', 'BigQuery', 'Redshift', 'Amplitude']
    };

    // In a real implementation, you would capture the diagram as an image
    // For this example, we'll use a placeholder
    await downloadPDF(sampleData);
  } catch (error) {
    console.error('Failed to generate example PDF:', error);
    throw error;
  }
};
